/**
 * Suggestions for the city field in the job wizard.
 *
 * Fills a native <datalist> from `/mesta.json`. Native because the browser
 * already knows how to open it with the keyboard, how to render it on a phone
 * and how to keep it out of the way of a screen reader — a hand-built dropdown
 * has to reimplement all three and usually gets one of them wrong.
 *
 * Progressive enhancement, like everything else in this bundle: without it the
 * field is a text input and the wizard works exactly as before. The register is
 * a convenience for the person typing, never a gate — the server resolves
 * whatever it can and keeps the typed text either way.
 */

const MIN_QUERY = 2;
const DEBOUNCE_MS = 180;

export function initCitySuggest() {
  const input = document.querySelector("[data-city-suggest]");
  if (!input) return;

  const list = input.list;
  if (!list) return;

  const endpoint = input.dataset.citySuggest;
  let timer = null;
  let inFlight = null;
  // Keyed by the query, so going back to something already typed — which
  // backspacing does constantly — costs nothing.
  const seen = new Map();

  function render(matches) {
    list.replaceChildren();
    for (const match of matches) {
      const option = document.createElement("option");
      option.value = match.name;
      // Shown beside the name where the browser supports it. There are eleven
      // Lhotas; without the region the list is eleven identical rows.
      option.label = match.region;
      list.append(option);
    }
  }

  async function lookup(term) {
    if (seen.has(term)) {
      render(seen.get(term));
      return;
    }

    // One request at a time: the previous answer is for a shorter prefix and
    // would overwrite the newer one if it happened to land later.
    inFlight?.abort();
    const abort = new AbortController();
    inFlight = abort;

    try {
      const response = await fetch(`${endpoint}?q=${encodeURIComponent(term)}`, {
        signal: abort.signal,
        credentials: "omit",
      });
      if (!response.ok) return;
      const matches = await response.json();
      seen.set(term, matches);
      render(matches);
    } catch {
      // A failed lookup leaves the last suggestions in place and the field
      // usable. There is nothing to tell the user about: they can type.
    }
  }

  input.addEventListener("input", () => {
    const term = input.value.trim();
    clearTimeout(timer);
    if (term.length < MIN_QUERY) {
      list.replaceChildren();
      return;
    }
    timer = setTimeout(() => lookup(term), DEBOUNCE_MS);
  });
}
